import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { onAuthStateChanged, updateProfile } from 'firebase/auth';
import { auth, ensureUserDefaults, listenToUserProfile, listenToUserRides, listenToUserIntertownRequests, listenToPlatformConfig, updateUserProfile } from '../services/firebase';
import {
  APP, applyRemoteConfig, clearSelectedTownId, readSelectedTownId,
} from '../config/app';
import { syncServiceAreaCircles } from '../services/serviceAreaCircles';
import { applyZoneById } from '../services/maps';
import { initPassengerMessaging, listenForegroundMessages, updateLiveRideNotification, clearLiveRideNotification } from '../services/notifications';
import { asDisplayName as asName } from '../utils/name';
import {
  getCachedUserProfile, getCachedUserRecord, canUnlockFromCache,
  setCachedUserProfile, clearCachedUserProfile,
} from '../utils/passengerProfileCache';

const AppContext = createContext(null);
const AWAITING_NAME_KEY = 'mytownly_rides_awaiting_name';
const LOCAL_NAME_KEY = 'mt_rides_user_name';

/** ms since epoch for a Firestore Timestamp | number | {seconds} | ISO string. */
const tsToMillis = (u) => {
  if (!u) return null;
  if (typeof u === 'number') return u;
  if (typeof u.toMillis === 'function') return u.toMillis();
  if (u.seconds != null) return u.seconds * 1000;
  if (typeof u === 'string') { const t = Date.parse(u); return Number.isNaN(t) ? null : t; }
  return null;
};

export const markAwaitingName = (uid) => {
  try { sessionStorage.setItem(AWAITING_NAME_KEY, uid); } catch { /* */ }
};
export const clearAwaitingName = () => {
  try { sessionStorage.removeItem(AWAITING_NAME_KEY); } catch { /* */ }
};

// `asName` = utils/name.asDisplayName: a real name, not a phone number / symbols
// / a generic placeholder — '' otherwise, which routes the passenger to the name
// step to enter a real one.

// The local name cache is per-device; tag it with the uid it belongs to so a
// name cached by one passenger can't heal a different passenger's profile on a
// shared phone / browser.
export const readLocalName = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LOCAL_NAME_KEY) || 'null');
    if (parsed && parsed.uid && parsed.uid === auth.currentUser?.uid) return asName(parsed.name);
    return '';
  } catch { return ''; }
};

export const rememberLocalName = (name) => {
  try {
    const cleaned = asName(name);
    const uid = auth.currentUser?.uid;
    if (cleaned && uid) localStorage.setItem(LOCAL_NAME_KEY, JSON.stringify({ uid, name: cleaned }));
  } catch { /* */ }
};

const usableRideName = (rides) => {
  const hit = (rides || []).find((r) => asName(r?.customerName));
  return hit ? asName(hit.customerName) : '';
};

export const resolveDisplayName = (profile, firebaseUser, rides) => (
  asName(profile?.name)
  || asName(firebaseUser?.displayName)
  || readLocalName()
  || usableRideName(rides)
  || ''
);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(() => getCachedUserProfile(auth.currentUser?.uid));
  const [authLoading, setAuthLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(false);
  // True once the real Firestore profile has arrived, or loaded from a
  // recently-verified local cache (see canUnlockFromCache) — not the
  // optimistic phone-only stub set further down.
  const [profileLoaded, setProfileLoaded] = useState(
    () => canUnlockFromCache(getCachedUserRecord(auth.currentUser?.uid)),
  );
  const [rides, setRides] = useState([]);
  const [ridesLoaded, setRidesLoaded] = useState(false);
  const [ridesHasMore, setRidesHasMore] = useState(false);
  const [intertownRequests, setIntertownRequests] = useState([]);

  const defaultsEnsuredUidRef = useRef(null);

  const seedUserProfile = useCallback((partial) => {
    if (!partial?.id) return;
    defaultsEnsuredUidRef.current = partial.id;
    const hasName = Boolean(asName(partial.name));
    setUserProfile((prev) => ({
      ...(prev || {}),
      id: partial.id,
      phone: partial.phone || prev?.phone || '',
      name: partial.name || prev?.name || '',
      profileComplete: hasName || Boolean(prev?.profileComplete),
    }));
    setProfileReady(true);
    if (hasName) {
      rememberLocalName(partial.name);
      clearAwaitingName();
      setProfileLoaded(true);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      if (!firebaseUser) {
        setUserProfile(null);
        setProfileReady(true);
        setProfileLoaded(false);
        clearAwaitingName();
        defaultsEnsuredUidRef.current = null;
        clearCachedUserProfile();
        return;
      }
      const phone = firebaseUser.phoneNumber || '';
      setUserProfile((prev) => (
        prev?.id === firebaseUser.uid ? prev : { id: firebaseUser.uid, phone }
      ));
      setProfileReady(true);

      // Fast path: a passenger the server confirmed not-blocked within
      // OPTIMISTIC_PROFILE_MAX_AGE_MS skips the splash screen right now,
      // painting from cache while the real check below still runs in the
      // background — it will confirm, correct (-> BlockedScreen), or on a
      // transient failure just leave this in place for the next attempt.
      const cachedRecord = getCachedUserRecord(firebaseUser.uid);
      if (canUnlockFromCache(cachedRecord)) {
        setUserProfile(cachedRecord.profile);
        setProfileLoaded(true);
      }

      if (defaultsEnsuredUidRef.current === firebaseUser.uid) {
        // Defaults and user doc were already created server-side in verifyTruecallerToken.
        // Skip duplicate ensureUserDefaults write; live snapshot in listenToUserProfile handles sync.
        return;
      }
      ensureUserDefaults(firebaseUser.uid, phone).then((profile) => {
        if (profile) {
          setUserProfile(profile);
          setProfileLoaded(true);
          setCachedUserProfile(profile, { verified: true });
        }
        if (profile?.name?.trim()) {
          rememberLocalName(profile.name);
          clearAwaitingName();
        }
      }).catch(() => {});
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setRides([]);
      setProfileLoaded(false);
      return undefined;
    }
    return listenToUserProfile(user.uid, (profile, metadata = {}) => {
      if (profile) {
        setUserProfile(profile);
        setProfileLoaded(true);
        // Only an authoritative (non-cache-echo) snapshot bumps verifiedAt —
        // see setCachedUserProfile.
        setCachedUserProfile(profile, { verified: Boolean(metadata.authoritative) });
        if (profile.name?.trim()) rememberLocalName(profile.name);
      }
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setRides([]);
      setRidesLoaded(false);
      setRidesHasMore(false);
      return undefined;
    }
    let lastSig = '';
    // Startup only needs the current trip + a handful of recent rides for
    // active-ride detection, name-heal and the Book "recent places" chips.
    // The Rides tab loads its own full history page when opened.
    return listenToUserRides(user.uid, (data, page) => {
      setRidesLoaded(true);
      setRidesHasMore(Boolean(page?.hasMore));
      // During an active trip the ride doc updates constantly (driver location,
      // etc.). Only push a new array into context when something the app renders
      // from the list actually changed — otherwise every consumer of useApp()
      // re-renders a few times a second for nothing.
      const sig = (data || [])
        .map((r) => `${r.id}:${r.status}:${r.fare || ''}:${r.rating || ''}:${r.driverId || ''}`)
        .join('|');
      if (sig === lastSig) return;
      lastSig = sig;
      setRides(data);
    }, undefined, { pageSize: 8 });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setIntertownRequests([]);
      return undefined;
    }
    // Book needs to know about an active Intertown request the same way
    // Intertown already knows about an active local ride (activeRide below) —
    // the two bookings must never coexist, and this is what lets Book disable
    // its own CTA instead of relying only on the server to reject it.
    return listenToUserIntertownRequests(user.uid, setIntertownRequests);
  }, [user?.uid]);

  const [platform, setPlatform] = useState({ mode: 'normal', message: '', releaseNotice: null });
  const [platformConfigReady, setPlatformConfigReady] = useState(false);
  const [platformConfigError, setPlatformConfigError] = useState(false);
  // Bumping this re-opens the config listener. Firestore ends a listener after
  // a read error, so without it the app would get no config (towns, pause,
  // fares) for the rest of the session.
  const [platformConfigAttempt, setPlatformConfigAttempt] = useState(0);
  const platformConfigFailuresRef = useRef(0);
  const retryPlatformConfig = useCallback(() => {
    setPlatformConfigError(false);
    setPlatformConfigAttempt((n) => n + 1);
  }, []);
  const [bookingFlowActive, setBookingFlowActive] = useState(false);
  const savedTownAppliedRef = useRef(false);
  useEffect(() => {
    // config/platform is intentionally authenticated in Firestore rules. Do not
    // subscribe during auth restoration, otherwise the permission-denied echo
    // can incorrectly look like a missing release setting.
    if (!user?.uid) {
      setPlatform({ mode: 'normal', message: '', releaseNotice: null });
      setPlatformConfigReady(false);
      setPlatformConfigError(false);
      savedTownAppliedRef.current = false;
      platformConfigFailuresRef.current = 0;
      return undefined;
    }
    let reconnectTimer = null;
    const unsubscribe = listenToPlatformConfig((cfg, { fromServer = false, error = null } = {}) => {
      // A failed read (or a missing document) is not proof that a saved town
      // was removed. Keep the last good config and the rider's preference.
      if (error || !cfg) {
        // A cache miss is normal while Firestore is still reaching the server;
        // let Book's six-second wait handle that without flashing an error.
        if (error || fromServer) setPlatformConfigError(true);
        if (error) {
          // Reconnect by itself, waiting longer each time: 2 s, 4 s, 8 s … 60 s.
          const delayMs = Math.min(60000, 2000 * (2 ** platformConfigFailuresRef.current));
          platformConfigFailuresRef.current += 1;
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => setPlatformConfigAttempt((n) => n + 1), delayMs);
        }
        return;
      }
      platformConfigFailuresRef.current = 0;
      setPlatformConfigError(false);
      applyRemoteConfig(cfg);
      // A saved town can only be resolved after the live config supplies the
      // current zones. Do this once; later config snapshots must not silently
      // pull a passenger away from the town on screen. manual: false because
      // it is remembered from an earlier launch, not picked in this session,
      // so Book's first GPS fix may still move a rider who has since
      // travelled to another live town (utils/zones.js gpsMayChangeTown).
      if (!savedTownAppliedRef.current) {
        const savedTownId = readSelectedTownId();
        if (savedTownId) {
          const zone = applyZoneById(savedTownId, { manual: false });
          if (zone && zone.active !== false) savedTownAppliedRef.current = true;
          else if (fromServer) {
            clearSelectedTownId();
            savedTownAppliedRef.current = true;
          }
        } else {
          savedTownAppliedRef.current = true;
        }
      }
      // A cached config can be older than the rider's saved town. Wait for a
      // server answer before deciding it disappeared or showing a wrong town.
      const savedTownId = readSelectedTownId();
      const savedTownUnresolved = savedTownId && !savedTownAppliedRef.current;
      setPlatformConfigReady(!savedTownUnresolved);
      // Resize every currently-drawn service-area circle to match — previously
      // a live serviceRadiusKm change only took effect on the next map re-init
      // (an app reopen), even though the booking-eligibility check itself
      // (isWithinServiceArea) already read the new value immediately.
      syncServiceAreaCircles();
    // APP.mode / APP.modeMessage are set by applyRemoteConfig (single place for
    // the derivation). Mirror them into React state so the Book screen's notice
    // updates live when an admin pauses / resumes.
      setPlatform({
        mode: APP.mode || 'normal',
        message: APP.modeMessage || '',
        releaseNotice: cfg?.releaseNotice || null,
        intertown: cfg?.intertown || null,
        // Raw, not APP.mapCenter: a passenger's chosen town moves that, but
        // the server measures the Intertown pickup radius from these.
        mapCenterLat: cfg?.mapCenterLat ?? null,
        mapCenterLng: cfg?.mapCenterLng ?? null,
      });
    });
    return () => {
      clearTimeout(reconnectTimer);
      unsubscribe();
    };
  }, [user?.uid, platformConfigAttempt]);

  // Warm the Maps JS SDK during the splash / auth / profile load so the first
  // map paint (Book is the home screen) isn't waiting on a ~300KB cold download.
  // The SDK script itself isn't billed — only map instances and API calls are.
  useEffect(() => {
    import('../services/maps').then((m) => m.loadGoogleMaps()).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    // Silent token refresh if already allowed — don't force the system prompt here.
    initPassengerMessaging(user.uid, { request: false }).catch(() => {});
    return listenForegroundMessages((payload) => {
      const title = payload?.notification?.title;
      const body = payload?.notification?.body;
      const pushRideId = payload?.data?.rideId;
      // If the user is already watching this ride, the ActiveRide screen shows
      // its own status banner + toasts — a second toast from the push just
      // stacks up (and can be a late/stale one, e.g. "finding another Anna"
      // landing after the new driver already arrived).
      const onThisRide = pushRideId
        && typeof window !== 'undefined'
        && window.location?.pathname === `/active/${pushRideId}`;
      if (title && !onThisRide) toast(`${title}${body ? ` — ${body}` : ''}`, { duration: 5000 });
      // Nudge ride list refresh when a status push arrives (fixes stale home/active).
      const rideId = pushRideId;
      if (rideId) {
        import('../services/firebase').then(({ getRideFresh }) => {
          getRideFresh(rideId).then((fresh) => {
            if (!fresh) return;
            setRides((prev) => {
              const list = Array.isArray(prev) ? [...prev] : [];
              const idx = list.findIndex((r) => r.id === fresh.id);
              if (idx >= 0) list[idx] = { ...list[idx], ...fresh };
              else list.unshift(fresh);
              return list;
            });
          }).catch(() => {});
        });
      }
    });
  }, [user?.uid]);

  // Heal missing (or invalid — e.g. a phone number saved on an old build)
  // Firestore name from Auth / localStorage / prior rides.
  useEffect(() => {
    if (!user?.uid || !profileLoaded) return undefined;
    const firestoreName = asName(userProfile?.name);
    if (firestoreName) {
      rememberLocalName(firestoreName);
      clearAwaitingName();
      return undefined;
    }
    // A junk Firestore name is NOT a usable fallback — only a real one from Auth
    // / cache / a prior ride. If none, leave it: `needsName` sends the passenger
    // to the name step to enter a real one.
    const fallback = asName(user.displayName) || readLocalName() || usableRideName(rides);
    if (!fallback) return undefined;

    let cancelled = false;
    (async () => {
      try {
        await updateUserProfile(user.uid, { name: fallback, profileComplete: true });
        if (!user.displayName?.trim()) {
          await updateProfile(user, { displayName: fallback }).catch(() => {});
        }
        rememberLocalName(fallback);
        clearAwaitingName();
        if (!cancelled) {
          setUserProfile((prev) => ({
            ...(prev || {}),
            id: user.uid,
            name: fallback,
            profileComplete: true,
          }));
        }
      } catch (e) {
        console.warn('name heal failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [user, profileLoaded, userProfile?.name, rides]);

  const resolvedName = resolveDisplayName(userProfile, user, rides);
  const needsName = Boolean(
    profileReady && user && profileLoaded && !resolvedName,
  );

  const activeRide = rides.find((r) =>
    ['searching', 'driver_assigned', 'arrived', 'started'].includes(r.status),
  ) || null;

  // Mirrors intertown.js's ACTIVE_STATUSES / Intertown.js's own ACTIVE set.
  const activeIntertownRequest = intertownRequests.find((r) => [
    'pending_admin_confirmation', 'open_for_claim', 'awaiting_driver_acceptance', 'confirmed', 'arrived_at_pickup',
    'started', 'at_destination', 'returning',
  ].includes(r.status)) || null;

  // App-wide owner of the live-trip foreground service: it must run whenever a
  // ride is live, regardless of which screen the user is on (they may back out
  // to Home mid-trip). ActiveRide still refines the card while it's mounted.
  const liveFgStatusRef = useRef(null);
  useEffect(() => {
    const status = ['driver_assigned', 'arrived', 'started'].includes(activeRide?.status)
      ? activeRide.status
      : null;
    if (!status) {
      if (liveFgStatusRef.current) clearLiveRideNotification();
      liveFgStatusRef.current = null;
      return undefined;
    }
    liveFgStatusRef.current = status;
    const push = () => updateLiveRideNotification({
      rideId: activeRide.id,
      status,
      vehicleNumber: activeRide.vehicleNumber || '',
      driverName: activeRide.driverName || '',
      otp: activeRide.otp || '',
      pickupName: activeRide.pickup?.name || activeRide.pickup?.address || '',
      destinationName: activeRide.destination?.name || activeRide.destination?.address || '',
      startedAtMs: tsToMillis(activeRide.startedAt),
      durationMin: activeRide.durationMin || null,
    });
    push();
    // Keep the trip bar advancing on time even when ActiveRide isn't mounted
    // (user backed out to Home mid-trip). ActiveRide refines it with a live ETA
    // while it's on screen.
    if (status !== 'started') return undefined;
    const id = setInterval(push, 20000);
    return () => clearInterval(id);
  }, [activeRide?.id, activeRide?.status, activeRide?.vehicleNumber, activeRide?.driverName, activeRide?.startedAt, activeRide?.durationMin]);

  const refreshUserProfile = useCallback(async (uid, optimistic) => {
    const { getUserFast } = await import('../services/firebase');
    const profile = await getUserFast(uid);
    if (profile) {
      setUserProfile(profile);
      setProfileLoaded(true);
      if (profile.name?.trim()) rememberLocalName(profile.name);
      return profile;
    }
    if (optimistic) {
      setUserProfile((prev) => ({ ...(prev || {}), id: uid, ...optimistic }));
      setProfileLoaded(true);
      if (optimistic.name?.trim()) rememberLocalName(optimistic.name);
    }
    return profile;
  }, []);

  return (
    <AppContext.Provider value={{
      user, userProfile, authLoading, profileReady, profileLoaded, needsName, rides, ridesLoaded, ridesHasMore, activeRide, intertownRequests, activeIntertownRequest, refreshUserProfile, seedUserProfile,
      platform, platformMode: platform.mode, platformMessage: platform.message,
      releaseNotice: platform.releaseNotice, platformConfigReady, platformConfigError, retryPlatformConfig, bookingFlowActive, setBookingFlowActive,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
